import { Component, computed, OnDestroy, OnInit, Signal, ChangeDetectionStrategy } from "@angular/core";
import { Router, RouterLink, RouterLinkActive } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import { UpperCasePipe } from "@angular/common";
import { NgOptimizedImage } from "@angular/common";
import { ModalService } from "../../../services/modal.service";
import { NotificationService } from "../../../services/notification.service";
import { NavigateToMessagesService } from "../../../services/navigate-to-messages.service";
import { AppMessage } from "../../../models/app-message.model";
import { ActionButtonComponent } from "../../login/landing/action-button.component";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { PlatformService } from "../../../services/platform.service";
import { GeneralRequestService } from "../../../services/general-request.service";
import { VideoMeeting } from "../../../models/video-meeting.model";
import { VideoService } from "../../../services/video.service";
import { PaidServicesService } from "../../../services/paid-services.service";
import { UserInfoService } from "../../../services/user-info.service";
import { filterMessagesForContext } from "../../../shared/utils/message-visibility.utils";
import { NavItemPlatform } from "../../../models/nav-item-platform.model";

export type NavItemType = "messages" | "link" | "button";

export interface NavItem {
  type: NavItemType;
  labelKey: string;
  icon: string;
  iconAlt?: string;
  routerLink?: string;
  routerLinkExact?: boolean;
  platform: NavItemPlatform;
}

@Component({
  selector: "app-side-nav",
  templateUrl: "./side-nav.component.html",
  styleUrls: ["./side-nav.component.css"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    UpperCasePipe,
    NgOptimizedImage,
    ActionButtonComponent,
  ],
})
export class SideNavComponent implements OnInit, OnDestroy {
  areNewMessages = false;
  unreadMessagesCount = 0;
  videoVisitsCount = 0;
  messagesList: AppMessage[] = [];
  isMessagesActive = false;
  readonly brandName!: Signal<string>;
  readonly navItems!: Signal<NavItem[]>;
  private allVideoMeetings: VideoMeeting[] = [];

  readonly allNavItems: NavItem[] = [
    {
      type: "link",
      labelKey: "sideBottomNav.services",
      icon: "assets/header-icons/Services.svg",
      routerLink: "/", // Overridden in navItems$ based on platform
      routerLinkExact: true,
      platform: "always",
    },
    {
      type: "messages",
      labelKey: "sideBottomNav.messages",
      icon: "assets/header-icons/Messages.svg",
      platform: "always",
    },
    {
      type: "link",
      labelKey: "sideBottomNav.myHealth",
      icon: "assets/header-icons/MyHealth.svg",
      routerLink: "/my-health",
      routerLinkExact: false,
      platform: "epak",
    },
    {
      type: "link",
      labelKey: "sideBottomNav.videovisits",
      icon: "assets/header-icons/VideoVisits.svg",
      routerLink: "/video",
      routerLinkExact: false,
      platform: "always",
    },
    {
      type: "link",
      labelKey: "sideBottomNav.orderHistory",
      icon: "assets/header-icons/OrderHistory.svg",
      routerLink: "/paid/my-paid-services",
      routerLinkExact: false,
      platform: "paid",
    },
    {
      type: "link",
      labelKey: "sideBottomNav.weightInfo",
      icon: "assets/header-icons/WeightScales.svg",
      routerLink: "/paid/weight-info",
      routerLinkExact: true,
      platform: "paid",
    },
    {
      type: "link",
      labelKey: "sideBottomNav.myAccount",
      icon: "assets/header-icons/Account.svg",
      routerLink: "/user-profile",
      routerLinkExact: false,
      platform: "always",
    },
    {
      type: "link",
      labelKey: "sideBottomNav.myConsents",
      icon: "assets/header-icons/MyConsents.svg",
      routerLink: "/consents",
      routerLinkExact: false,
      platform: "always",
    },
    // Fitbit integration disabled (EPAK-2253)
    // {
    //   type: "link",
    //   labelKey: "sideBottomNav.connectedDevices",
    //   icon: "assets/header-icons/MyDevices.svg",
    //   routerLink: "/my-apps",
    //   routerLinkExact: false,
    //   platform: "epak",
    // },
    {
      type: "button",
      labelKey: "sideBottomNav.logOut",
      icon: "assets/header-icons/LogOut.svg",
      platform: "always",
    },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private modalService: ModalService,
    private notificationService: NotificationService,
    private generalRequestService: GeneralRequestService,
    private videoService: VideoService,
    private paidServicesService: PaidServicesService,
    private userInfoService: UserInfoService,
    private navigateToMessages: NavigateToMessagesService,
    private router: Router,
    private platformService: PlatformService,
  ) {
    this.brandName = this.platformService.brandName;
    this.navItems = computed(() => {
      const brandName = this.platformService.brandName();
      const isPaid = brandName === this.platformService.paidPlatform;
      const isEpak = brandName === this.platformService.epakPlatform;
      return this.allNavItems
        .filter((item) => {
          const isExternal = this.userInfoService.getIsExternalPatient();
          if (isExternal && (item.labelKey === "sideBottomNav.myHealth" || item.labelKey === "sideBottomNav.connectedDevices")) {
            return false;
          }
          if (item.platform === "always") return true;
          if (item.platform === "paid") return isPaid;
          if (item.platform === "epak") return isEpak;
          return false;
        })
        .map((item) => {
          if (item.labelKey === "sideBottomNav.services") {
            return { ...item, routerLink: isPaid ? "/paid" : "/" };
          }
          return item;
        });
    });
  }

  ngOnInit(): void {
    this.subscribeToNotifications();
    this.subscribeToVideoMeetings();
    void this.paidServicesService.fetchPremiumRegisterIds().then(() => this.updateVideoBadgeCount());
    this.updateActiveState();
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateActiveState());
  }

  private updateActiveState(): void {
    this.isMessagesActive = this.router.url.includes("/message");
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToNotifications(): void {
    this.notificationService
      .getNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe((messages: AppMessage[]) => {
        this.messagesList = messages;
        this.checkIfHasNewMessages();
      });
  }

  private subscribeToVideoMeetings(): void {
    this.generalRequestService
      .getVideoMeetings()
      .pipe(takeUntil(this.destroy$))
      .subscribe((meetings: VideoMeeting[] | null) => {
        this.allVideoMeetings = meetings ?? [];
        this.updateVideoBadgeCount();
      });
  }

  private updateVideoBadgeCount(): void {
    const premiumIds = this.paidServicesService.premiumRegisterIds;
    const isPaidPlatform = this.platformService.brandName() === this.platformService.paidPlatform;
    this.videoVisitsCount = this.videoService.getVisibleMeetingsCountForPlatform(
      this.allVideoMeetings,
      premiumIds,
      isPaidPlatform,
    );
  }

  private checkIfHasNewMessages(): void {
    const visibleMessages = filterMessagesForContext(this.messagesList, {
      isPaidPlatform: this.platformService.brandName() === this.platformService.paidPlatform,
      isExternalPatient: this.userInfoService.getIsExternalPatient(),
    });
    const unread = visibleMessages.filter(
      (m) => m.isUnread && m.directionToPatient
    );
    this.areNewMessages = unread.length > 0;
    this.unreadMessagesCount = unread.length;
  }

  onMessagesClick(): void {
    this.navigateToMessages.navigateToMessages();
  }

  onLogout(): void {
    this.modalService.showLogoutConfirmModal();
  }
}
